import React, { useState, useCallback, useRef, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import Step4Plan from '../onboarding/steps/Step4Plan';
import Step5Devices from '../onboarding/steps/Step5Devices';
import Step6Checkout from '../onboarding/steps/Step6Checkout';
import Step7Receipt from '../onboarding/steps/Step7Receipt';
import Step8Pending from '../onboarding/steps/Step8Pending';
import Step9Activated from '../onboarding/steps/Step9Activated';
import { BRAND } from '../../components/onboarding/primitives';
import { paymentOrdersAPI } from '../../services/api';

/**
 * SubscriptionChangeFlow — multi-step renewal / upgrade for an EXISTING
 * tenant. Mirrors the new-account onboarding payment flow exactly (Steps
 * 4–9) — same plan picker, same devices step, same receipt-upload + admin
 * approval — but skips Steps 1–3 (account creation, lab info, branches)
 * since the tenant already has those filled in.
 *
 * Mounted at:
 *   /settings/subscription/renew    (when subscription is expiring)
 *   /settings/subscription/upgrade  (when on free tier, wants to upgrade)
 *
 * Both routes render the same component — the data flow (plan → draft →
 * submit → receipt → approval) is identical. The opening copy adapts to
 * the entry path so the user knows whether they're "renewing" or
 * "upgrading", but everything else is shared.
 *
 * On success (Step 9), the user is sent back to /settings/subscription
 * rather than the new-account /onboarding/setup wizard.
 */
const STATE_KEY = 'lis_subscription_change_state';
const STEP_KEY = 'lis_subscription_change_step';

// Same shape as OnboardingFlow but only the steps we use are populated.
const defaultState = () => ({
  s1: {},
  s2: {},
  s3: {},
  s4: { plan: null, cycle: 'yearly' },
  s5: { deviceCount: null },
  s6: {},
  s7: {},
});

function loadState() {
  try {
    const raw = localStorage.getItem(STATE_KEY);
    if (!raw) return defaultState();
    return { ...defaultState(), ...JSON.parse(raw) };
  } catch {
    return defaultState();
  }
}

function loadInitialStep() {
  const raw = localStorage.getItem(STEP_KEY);
  const n = parseInt(raw, 10);
  if (!isNaN(n) && n >= 4 && n <= 9) return n;
  return 4;
}

export default function SubscriptionChangeFlow() {
  const navigate = useNavigate();
  const location = useLocation();
  const [state, setStateRaw] = useState(loadState);
  const [current, setCurrent] = useState(loadInitialStep);
  const historyRef = useRef([]);
  const cancelGuardRef = useRef(false);

  // Distinguish renewal vs upgrade purely from the route we mounted under,
  // so the copy on Step 9 matches the user's intent (came in to "renew"
  // → say "renewed"; came in to "upgrade" → say "upgraded").
  const mode = location.pathname.includes('upgrade') ? 'upgrade' : 'renew';

  // Persist scratchpad + current step so a refresh mid-flow doesn't wipe
  // progress. Mirrors OnboardingFlow's behaviour.
  useEffect(() => {
    try {
      localStorage.setItem(STATE_KEY, JSON.stringify(state));
    } catch { /* ignore */ }
  }, [state]);
  useEffect(() => {
    try {
      localStorage.setItem(STEP_KEY, String(current));
    } catch { /* ignore */ }
  }, [current]);

  // Cancel any stale draft on first mount so the user starts clean. Without
  // this, opening renew after an aborted previous attempt would skip the
  // plan picker because `getMyActive()` returns the abandoned draft. Guarded
  // by a ref so React 18 StrictMode's double-invoke doesn't fire twice.
  useEffect(() => {
    if (cancelGuardRef.current) return;
    cancelGuardRef.current = true;
    paymentOrdersAPI.cancelDraft?.().catch(() => {
      // 404 / 409 are expected when no active draft exists; ignore.
    });
  }, []);

  const setState = useCallback((updater) => {
    setStateRaw((prev) => (typeof updater === 'function' ? updater(prev) : updater));
  }, []);

  const goToStep = useCallback((n) => {
    if (n === 'dashboard') {
      // Clear scratchpad and head back to subscription settings.
      try {
        localStorage.removeItem(STATE_KEY);
        localStorage.removeItem(STEP_KEY);
      } catch { /* ignore */ }
      navigate('/settings/subscription', { replace: true });
      return;
    }
    setCurrent((prev) => {
      // Same history bookkeeping as OnboardingFlow — record forward jumps,
      // pop on back jumps, so goBack feels right.
      if (n > prev) {
        historyRef.current.push(prev);
      } else if (n < prev) {
        while (historyRef.current.length > 0
          && historyRef.current[historyRef.current.length - 1] >= n) {
          historyRef.current.pop();
        }
      }
      return n;
    });
  }, [navigate]);

  const goBack = useCallback(() => {
    setCurrent((prev) => {
      // From step 4, going back leaves the flow entirely. Also bail with a
      // confirmation prompt so an accidental click doesn't drop their work.
      if (prev <= 4) {
        const confirmed = window.confirm('سيتم إلغاء التجديد. هل تريد الخروج؟');
        if (!confirmed) return prev;
        try {
          localStorage.removeItem(STATE_KEY);
          localStorage.removeItem(STEP_KEY);
        } catch { /* ignore */ }
        // Best-effort cancel of the draft we may have created.
        paymentOrdersAPI.cancelDraft?.().catch(() => {});
        navigate('/settings/subscription', { replace: true });
        return prev;
      }
      while (historyRef.current.length > 0
        && historyRef.current[historyRef.current.length - 1] >= prev) {
        historyRef.current.pop();
      }
      const last = historyRef.current.pop();
      if (typeof last === 'number') return last;
      return Math.max(4, prev - 1);
    });
  }, [navigate]);

  // When the flow lands on Step 9 (success), wipe the scratchpad. The user
  // should not see resumed renewal state next time they hit the page.
  useEffect(() => {
    if (current !== 9) return;
    try {
      localStorage.removeItem(STATE_KEY);
      localStorage.removeItem(STEP_KEY);
    } catch { /* ignore */ }
  }, [current]);

  const stepProps = { state, setState, goToStep, goBack };

  const rejectionReason = state.activeOrder?.rejectionReason || null;
  const isRejected = state.activeOrder?.status === 'rejected';

  let StepComponent;
  switch (current) {
    case 4: StepComponent = <Step4Plan {...stepProps} />; break;
    case 5: StepComponent = <Step5Devices {...stepProps} />; break;
    case 6: StepComponent = <Step6Checkout {...stepProps} />; break;
    case 7: StepComponent = <Step7Receipt {...stepProps} />; break;
    case 8: StepComponent = <Step8Pending state={state} rejected={isRejected} rejectionReason={rejectionReason} goToStep={goToStep} />; break;
    case 9: StepComponent = (
      <Step9Activated
        title={mode === 'upgrade' ? 'تمّت ترقية الباقة!' : 'تمّ تجديد الاشتراك!'}
        subtitle={mode === 'upgrade'
          ? 'الباقة الجديدة فعّالة الآن — يمكنك مواصلة استخدام النظام بكامل المزايا.'
          : 'فترة الاشتراك تم تمديدها. شكراً لاستمرار ثقتك بنا.'}
        ctaLabel="العودة لإعدادات الاشتراك"
        ctaPath="/settings/subscription"
      />
    ); break;
    default:
      // Defensive fallback — should never happen since we clamp at 4.
      toast.error('خطأ في تحديد الخطوة');
      navigate('/settings/subscription', { replace: true });
      StepComponent = null;
  }

  return (
    <div dir="rtl" style={{ fontFamily: 'inherit', minHeight: '100vh', background: BRAND.surface }}>
      {StepComponent}
    </div>
  );
}
